import React, { useEffect, useState } from 'react';
import { ArrowRight, Sparkles, Play } from 'lucide-react';
import { Button } from './ui/button';
import { heroData, videoUrl } from '../utils/mock';

export const Hero = () => {
  const [showVideo, setShowVideo] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Video Background Option */}
      {showVideo ? (
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
      ) : (
        // Image Background
        <div className="absolute inset-0 z-0">
          <img
            src={heroData.image}
            alt="Luxury Interior"
            className="w-full h-full object-cover animate-ken-burns"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20">
        <div className="max-w-3xl">
          {/* Badge with animation */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6 animate-fade-in-down">
            <Sparkles className="h-4 w-4 text-rose-400 animate-pulse" />
            <span className="text-sm text-white font-medium">
              {heroData.subtitle}
            </span>
          </div>

          {/* Main Heading with stagger animation */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            <span className="block animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Transform Your
            </span>
            <span className="block animate-fade-in-up bg-gradient-to-r from-rose-400 via-pink-400 to-orange-400 bg-clip-text text-transparent" style={{ animationDelay: '0.2s' }}>
              Space Into Art
            </span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-2xl animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            {heroData.description}
          </p>

          {/* CTA Buttons with stagger */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <Button
              onClick={() => scrollToSection('portfolio')}
              className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white px-8 py-6 text-lg rounded-full shadow-2xl hover:shadow-rose-500/50 transition-all duration-300 group transform hover:scale-105"
            >
              View Our Work
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
            </Button>
            <Button
              onClick={() => setShowVideo(!showVideo)}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg rounded-full backdrop-blur-sm bg-white/10 transition-all duration-300 group"
            >
              <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
              {showVideo ? 'View Image' : 'Watch Video'}
            </Button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-6 mt-12 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            {[
              { number: '50+', label: 'Projects' },
              { number: '40+', label: 'Clients' },
              { number: '20+', label: 'Years' }
            ].map((stat, index) => (
              <div key={index} className="text-white group">
                <div className="text-3xl font-bold mb-1 group-hover:scale-110 transition-transform duration-300">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Animated scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white rounded-full animate-scroll-indicator"></div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10"></div>
    </section>
  );
};
